import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, Typography, Autocomplete, Paper,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listPriceListEntries, addPriceListEntry, type PriceListItem } from '../../api/preturi'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

/** FR-MDP-001, 009 — prețuri versionate în timp per articol, într-o listă de preț. */
export default function PriceListEntriesDialog({ list, onClose }: { list: PriceListItem | null; onClose: () => void }) {
  const open = list !== null
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: entries } = useQuery({
    queryKey: ['comercial', 'preturi', 'liste', list?.id, 'preturi'],
    queryFn: () => listPriceListEntries(list!.id),
    enabled: open,
  })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const [articleId, setArticleId] = useState<string | null>(null)
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => addPriceListEntry(list!.id, {
      articleId: articleId!, price: Number(price), currency, effectiveFrom, effectiveTo: null, reason: reason || null,
    }),
    onSuccess: () => {
      setArticleId(null); setPrice(''); setEffectiveFrom(''); setReason('')
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'liste', list?.id, 'preturi'] })
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'liste'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prețuri — {list?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
              getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
              value={articleId} onChange={(_, v) => setArticleId(v)}
              renderInput={(params) => <TextField {...params} label="Articol" size="small" />}
            />
            <TextField size="small" type="number" label="Preț" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ width: 110 }} />
            <TextField size="small" label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 90 }} />
            <TextField size="small" type="date" label="Valabil de la" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
            <Button variant="outlined" startIcon={<Add />} disabled={!articleId || !price || !effectiveFrom || mutation.isPending}
              onClick={() => mutation.mutate()}>
              Adaugă
            </Button>
          </Stack>
          <TextField size="small" label="Motiv (opțional, FR-ADM-012)" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth />

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack spacing={1}>
            {entries?.length === 0 && <Typography variant="body2" color="text.secondary">Niciun preț adăugat încă.</Typography>}
            {entries?.map((e) => (
              <Paper key={e.id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.articleCode} — {e.articleDescription}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.price} {e.currency} · valabil {e.effectiveFrom}{e.effectiveTo ? ` – ${e.effectiveTo}` : ' (fără dată de sfârșit)'}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
